import { z } from 'zod';

export const householdRoleSchema = z.enum(['owner', 'editor', 'viewer']);
export type HouseholdRole = z.infer<typeof householdRoleSchema>;

export interface HouseholdSummary {
  id: string;
  name: string;
  role: HouseholdRole;
  createdAt: number;
  joinedAt: number;
  /**
   * The household's plan, carried on the switcher payload so billing UI and the lock
   * screen work with no store open — and, because the provider caches this list, offline.
   */
  plan: PlanId;
}

export const planIdSchema = z.enum(['free', 'pro', 'locked']);
export type PlanId = z.infer<typeof planIdSchema>;

/** Note the two l's in `cancelled`, matching the D1 CHECK constraints. */
export const subscriptionStatusSchema = z.enum([
  'active',
  'trialing',
  'past_due',
  'paused',
  'cancelled',
  'expired',
]);
export type SubscriptionStatus = z.infer<typeof subscriptionStatusSchema>;

/** Where a subscription was bought. 'web' is RevenueCat Web Billing. */
export const subscriptionStoreSchema = z.enum([
  'complimentary',
  'web',
  'app_store',
  'play_store',
]);
export type SubscriptionStore = z.infer<typeof subscriptionStoreSchema>;

/**
 * The list price in USD, for surfaces that cannot ask a store SDK what to charge — the
 * marketing site, and a last-resort fallback while an offering is still loading.
 *
 * Not for anywhere a purchase can actually be made. Every store localises its own price,
 * so a screen with a live offering must render that offering's formatted price instead;
 * showing a number the checkout will not honour is both wrong and, on iOS, a review
 * rejection.
 */
export const SUBSCRIPTION_PRICE_USD = 39.99;

export const checkoutRequestSchema = z.object({ householdId: z.string() });

export interface CheckoutResponse {
  /** Nonce to attach as the `platewell_intent_id` RevenueCat subscriber attribute. */
  intentId: string;
  /** RevenueCat App User ID to log in as before purchasing: the Platewell user id. */
  appUserId: string;
}

export interface BillingSummary {
  householdId: string;
  plan: PlanId;
  status: 'none' | SubscriptionStatus;
  /** null means unlimited. */
  recipeLimit: number | null;
  /** Renewal (or expiry) of the current period. */
  currentPeriodEnd: number | null;
  cancelAtPeriodEnd: boolean;
  complimentary: boolean;
  /** Null when nothing has ever been purchased for this household. */
  store: SubscriptionStore | null;
  /**
   * Where the subscriber manages billing: RevenueCat's hosted portal for web purchases,
   * the App Store or Play Store account page for in-app ones. Null when there is nothing
   * to manage.
   */
  managementUrl: string | null;
  /** Display name of whoever bought it, so a co-owner isn't guessing whose card is on file. */
  subscribedBy: string | null;
  /** When the 3-day grace period started, if payment is currently failing. */
  pastDueSince: number | null;
  /**
   * What this household is actually billed in — ISO 4217, from the provider event rather
   * than from anything we guessed about the buyer's location. Null until something is
   * purchased, and on rows written before the currency was recorded.
   */
  currency: string | null;
  /**
   * The charged amount in `currency`'s minor units. Pair it with `currency` and
   * `formatMoney` — it is not reliably hundredths. Null when nothing has been charged.
   */
  amountMinor: number | null;
}

/** Short enough to sit on one line as the settings heading and in the household switcher. */
export const MAX_HOUSEHOLD_NAME_LENGTH = 50;

export const createHouseholdSchema = z.object({
  name: z.string().min(1).max(MAX_HOUSEHOLD_NAME_LENGTH),
});

/** Trimmed first, so a name of only spaces is refused rather than saved blank. */
export const renameHouseholdSchema = z.object({
  name: z.string().trim().min(1).max(MAX_HOUSEHOLD_NAME_LENGTH),
});

/** People per household, counting pending invites as well as members. */
export const MAX_HOUSEHOLD_MEMBERS = 10;

/** The free plan is for one person: nobody else can be invited until the household subscribes. */
export const FREE_HOUSEHOLD_MEMBERS = 1;

/**
 * How many people a household on `plan` can hold. Only the free plan is held to one: a
 * locked household is a paid one whose payment failed, and it keeps the people it has.
 */
export function householdMemberLimit(plan: PlanId): number {
  return plan === 'free' ? FREE_HOUSEHOLD_MEMBERS : MAX_HOUSEHOLD_MEMBERS;
}

export const createInviteSchema = z.object({
  email: z.string().email(),
  role: householdRoleSchema.default('editor'),
});

export const presignRequestSchema = z.object({
  blobId: z.string().regex(/^[0-9a-f]{64}$/, 'blobId must be a lowercase SHA-256 hex digest'),
  contentType: z.enum(['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/avif', 'image/jxl']),
  byteSize: z.number().int().positive().max(25 * 1024 * 1024),
  width: z.number().int().nonnegative().default(0),
  height: z.number().int().nonnegative().default(0),
  blurhash: z.string().max(200).default(''),
  householdId: z.string(),
});

export interface PresignResponse {
  /**
   * Absent when the blob is already uploaded — the client can skip straight to done.
   * In local development it is a path on the API, resolved against the API base URL.
   */
  uploadUrl: string | null;
  blobId: string;
  alreadyUploaded: boolean;
  expiresAt: number;
}

export const createShareSchema = z.object({
  householdId: z.string(),
  kind: z.enum(['recipe', 'collection']),
  recipeIds: z.array(z.string()).min(1).max(500),
  title: z.string().min(1).max(300),
});

export const importShareSchema = z.object({
  householdId: z.string(),
});

export interface RevisionSummaryDto {
  revisionNo: number;
  changedAt: number;
  changedBy: string[];
  changedFields: string[];
  rebuilt: boolean;
}

export interface ProjectionLagRow {
  householdId: string;
  docVersion: number;
  projectedVersion: number;
  lag: number;
  circuitOpen: boolean;
  failureCount: number;
  lastError: string | null;
  lastProjectedAt: number | null;
}

export const importFileStatusSchema = z.enum([
  'pending',
  'extracting',
  'ready',
  'in-progress',
  'success',
  'fail',
  'duplicate',
]);
export type ImportFileStatus = z.infer<typeof importFileStatusSchema>;

export const importSourceKindSchema = z.enum([
  'json',
  'html',
  'text',
  'pdf',
  'image',
  'url',
  'document',
]);
export type ImportSourceKind = z.infer<typeof importSourceKindSchema>;

export interface ImportFileDto {
  id: string;
  originalFilename: string;
  byteSize: number;
  status: ImportFileStatus;
  sourceKind: ImportSourceKind;
  recipeId: string | null;
  error: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface ImportSessionDto {
  id: string;
  householdId: string;
  createdBy: string;
  createdAt: number;
  files: ImportFileDto[];
}

export interface ImportSessionSummaryDto {
  id: string;
  householdId: string;
  createdBy: string;
  createdAt: number;
  fileCount: number;
  successCount: number;
  failCount: number;
  duplicateCount: number;
  pendingCount: number;
}

export const patchImportFileSchema = z.object({
  status: importFileStatusSchema,
  recipeId: z.string().optional(),
  error: z.string().max(2000).optional(),
});

export const importImageRequestSchema = z.object({
  url: z.string().url().max(4096),
});

/**
 * Structured recipe extraction from an LLM (or the JSON-LD URL fast path).
 * Validated server-side before the result blob is written for the client.
 */
export const extractedIngredientSchema = z.object({
  groupHeading: z.string().max(200).default(''),
  quantity: z.string().max(60).default(''),
  unit: z.string().max(60).default(''),
  name: z.string().max(300).default(''),
  note: z.string().max(500).default(''),
});

export const extractedInstructionSchema = z.object({
  text: z.string().max(100_000).default(''),
});

export const extractedNutritionFactSchema = z.object({
  label: z.string().max(200).default(''),
  amount: z.string().max(60).default(''),
  unit: z.string().max(60).default(''),
});

/**
 * Image references travel in the extraction result. A saved HTML page can inline
 * its photo as a `data:` URI rather than link to one, so an entry is allowed to
 * be a whole small image instead of just a URL.
 */
export const MAX_IMPORT_IMAGE_REF_CHARS = 3 * 1024 * 1024;

export const extractedRecipeSchema = z.object({
  title: z.string().min(1).max(300),
  description: z.string().max(100_000).default(''),
  servings: z.string().max(120).default(''),
  prepMinutes: z.number().int().nonnegative().nullable().default(null),
  cookMinutes: z.number().int().nonnegative().nullable().default(null),
  ingredients: z.array(extractedIngredientSchema).max(500).default([]),
  instructions: z.array(extractedInstructionSchema).max(500).default([]),
  tags: z.array(z.string().max(60)).max(50).default([]),
  nutritionBasis: z.enum(['perServing', 'perRecipe', 'unknown']).default('unknown'),
  nutritionFacts: z.array(extractedNutritionFactSchema).max(100).default([]),
  sourceType: z.enum(['none', 'url', 'text']).default('none'),
  sourceUrl: z.string().max(2048).default(''),
  sourceText: z.string().max(100_000).default(''),
  imageUrls: z.array(z.string().max(MAX_IMPORT_IMAGE_REF_CHARS)).max(10).default([]),
});
export type ExtractedRecipe = z.infer<typeof extractedRecipeSchema>;

export interface ImportImageResponse {
  blobId: string;
  contentType: string;
  byteSize: number;
  width: number;
  height: number;
}

export const MAX_IMPORT_FILES_PER_SESSION = 300;
/** Overall ceiling for any single import source file. */
export const MAX_IMPORT_FILE_BYTES = 20 * 1024 * 1024;
export const MAX_IMPORT_JSON_BYTES = 1024 * 1024;
/** Saved pages carry markup, inline styles and scripts, so they run larger than plain text. */
export const MAX_IMPORT_HTML_BYTES = 5 * 1024 * 1024;
/** A document's size is dominated by the photos it embeds, so it gets the PDF ceiling. */
export const MAX_IMPORT_DOCUMENT_BYTES = 20 * 1024 * 1024;
export const MAX_IMPORT_TEXT_BYTES = 1024 * 1024;
export const MAX_IMPORT_PDF_BYTES = 20 * 1024 * 1024;
export const MAX_IMPORT_IMAGE_FILE_BYTES = 20 * 1024 * 1024;
export const MAX_IMPORT_IMAGES_PER_RECIPE = 10;

/**
 * How long a file may sit at `extracting` before the run that claimed it is presumed
 * dead and it may be queued again.
 *
 * Shared by the client's retry and the server's maintenance sweep so the two agree on
 * when an extraction is dead rather than merely slow. Generous on purpose: re-queueing a
 * live extraction pays OpenAI for it twice, and a queue consumer's own wall clock runs
 * out long before this.
 */
export const STUCK_EXTRACTING_MS = 30 * 60 * 1000;

/**
 * URL imports arrive as a batch — the web paste box takes one URL per line.
 * Single-`url` bodies are still accepted for clients that only ever send one.
 */
export const urlImportRequestSchema = z
  .object({
    url: z.string().url().max(4096).optional(),
    urls: z.array(z.string().url().max(4096)).max(MAX_IMPORT_FILES_PER_SESSION).optional(),
  })
  .transform((body) => ({ urls: body.urls ?? (body.url == null ? [] : [body.url]) }))
  .refine((body) => body.urls.length > 0, { message: 'At least one URL is required' });

export const IMPORT_CONTENT_TYPES = {
  json: new Set(['application/json', 'application/ld+json', 'text/json']),
  html: new Set(['text/html', 'application/xhtml+xml']),
  document: new Set([
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.oasis.opendocument.text',
    // Word 97-2003 is not readable, but it is recognised here so the file lands as one
    // failed row explaining itself rather than rejecting the whole upload.
    'application/msword',
  ]),
  text: new Set(['text/plain', 'text/markdown']),
  pdf: new Set(['application/pdf']),
  image: new Set([
    'image/png',
    'image/jpeg',
    'image/webp',
    'image/avif',
    'image/heic',
    'image/heif',
  ]),
} as const;

export function maxBytesForImportKind(kind: ImportSourceKind): number {
  switch (kind) {
    case 'json':
      return MAX_IMPORT_JSON_BYTES;
    case 'html':
      return MAX_IMPORT_HTML_BYTES;
    case 'document':
      return MAX_IMPORT_DOCUMENT_BYTES;
    case 'text':
      return MAX_IMPORT_TEXT_BYTES;
    case 'pdf':
      return MAX_IMPORT_PDF_BYTES;
    case 'image':
    case 'url':
      return MAX_IMPORT_IMAGE_FILE_BYTES;
  }
}

/**
 * Per-minute OpenAI budget the paced client spends against, at ~90% of the
 * gpt-5.4 account limits (500,000 TPM / 500 RPM) so bursts keep some headroom.
 * These track the model pinned in `apps/api/src/imports/ai-extract.ts`; a model
 * change means revisiting them.
 */
export const OPENAI_TPM_BUDGET = 450_000;
export const OPENAI_RPM_BUDGET = 450;

/** Ceiling on any single estimate, so one bad guess cannot drain a whole minute. */
const MAX_ESTIMATED_EXTRACTION_TOKENS = 30_000;

/**
 * Rough token cost of running one import file through the extraction model, used
 * client-side to pace requests before they are sent. Estimates are deliberately
 * conservative: overshooting only slows the import, while undershooting risks a 429.
 */
export function estimateExtractionTokens(input: {
  sourceKind: ImportSourceKind;
  byteSize: number;
}): number {
  const { sourceKind, byteSize } = input;
  switch (sourceKind) {
    // Parsed by the client and never sent to the model.
    case 'json':
      return 0;
    // Roughly 4 bytes per token, bounded by the 100k-char truncation in `htmlToReadableText`.
    case 'text':
      return clampEstimate(byteSize / 4, 25_000);
    // Markup, styles and scripts are stripped before the model sees the page,
    // so raw bytes overstate the real prompt by a wide margin.
    case 'html':
      return clampEstimate(byteSize / 8, 25_000);
    // A document is a compressed archive that is mostly photo bytes, so its size says
    // very little about its text. Anything but a trivial file lands on the clamp.
    case 'document':
      return clampEstimate(byteSize / 8, 25_000);
    // The server fetches the page, so the client cannot see its size. Assume a
    // middling recipe article rather than the worst case, which would stall imports.
    case 'url':
      return 10_000;
    // Vision cost comes from tiling the rendered image, not from the file size,
    // and imports are scaled down to 2048px before extraction.
    case 'image':
      return 2_000;
    // Pages render to images alongside their text layer.
    case 'pdf':
      return clampEstimate((byteSize / 100_000) * 1_500, MAX_ESTIMATED_EXTRACTION_TOKENS);
  }
}

function clampEstimate(estimate: number, cap: number): number {
  if (!Number.isFinite(estimate) || estimate <= 0) return 1;
  return Math.min(Math.ceil(estimate), cap);
}
